import { useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { IconButton } from '../components/IconButton';
import { IconBack, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { StatementsPanel } from '../components/StatementsPanel';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface MemberRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  member_type: string | null;
}

// Menu-level entry point for Statements. A self-service Member always sees
// their own record — there's nothing to pick — while staff get the same
// searchable register as Members.tsx, each row linking to the same
// Mini/Detail/Annual tabs as the per-member "Statements" button on
// MemberDetail.tsx (see StatementsPanel).
export function Statements() {
  const { user } = useAuth();
  const { lists } = useConfig();
  const [search, setSearch] = useState('');
  const [memberType, setMemberType] = useState('');
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);

  const filters = useMemo(
    () => ({ search: search || undefined, member_type: memberType || undefined }),
    [search, memberType]
  );
  const { data, loading, error, setPage } = usePaginatedList<MemberRow>('/members', filters);

  if (user?.is_member && user.member_id) {
    return (
      <>
        <header className="page-header">
          <h1>Statements</h1>
        </header>
        <StatementsPanel memberId={user.member_id} memberName={user.full_name} />
      </>
    );
  }

  if (selected) {
    return (
      <>
        <header className="page-header">
          <div className="page-header-actions">
            <IconButton icon={IconBack} label="Back to members" onClick={() => setSelected(null)} />
            <h1>Statements — {selected.name}</h1>
          </div>
        </header>
        <StatementsPanel memberId={selected.id} memberName={selected.name} />
      </>
    );
  }

  return (
    <>
      <header className="page-header">
        <h1>Statements</h1>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name, email or mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={memberType} onChange={(e) => setMemberType(e.target.value)}>
          <option value="">All types</option>
          {listOptions(lists, 'member_type').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((m) => {
                  const name = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || 'Member';
                  return (
                    <tr key={m.id}>
                      <td>
                        <strong>{name}</strong>
                      </td>
                      <td>{m.email}</td>
                      <td>{labelFor(lists, 'member_type', m.member_type ?? '')}</td>
                      <td className="right">
                        <IconButton
                          icon={IconView}
                          label="View Statements"
                          onClick={() => setSelected({ id: m.id, name })}
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="muted center">
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
